import { api } from './axios';

/** @typedef {import('../model/battle').TrackReactionRequest} TrackReactionRequest */
/** @typedef {import('../model/battle').TrackReactionResponse} TrackReactionResponse */
/** @typedef {import('../model/battle').TrackReactionStatsResponse} TrackReactionStatsResponse */

const validateId = (id, name = 'ID') => {
    if (!id && id !== 0) throw new Error(`${name} is required`);
};

/**
 * Track Reaction API — emoji-реакции на треки в раундах баттла.
 *
 * Базовый путь: /api/battles/{battleId}/rounds/{roundId}/tracks/{trackId}/reactions
 *
 * Один пользователь может оставить несколько разных emoji на один трек.
 * Удаление по {emoji} снимает только конкретную реакцию,
 * удаление без параметра снимает все реакции пользователя на треке.
 *
 * @see api_docs_v2/battle/track_reaction_api.md
 */
export const trackReactionsApi = {
    /**
     * Добавить emoji-реакцию к треку от имени текущего пользователя.
     * @param {string} battleId — UUID баттла
     * @param {string} roundId — UUID раунда
     * @param {string} trackId — UUID трека
     * @param {Object} payload — TrackReactionRequest { emoji: string }
     * @returns {Promise<TrackReactionResponse>}
     */
    add: async (battleId, roundId, trackId, payload) => {
        validateId(battleId, 'Battle ID');
        validateId(roundId, 'Round ID');
        validateId(trackId, 'Track ID');
        if (!payload?.emoji) throw new Error('emoji is required');
        try {
            const { data } = await api.post(
                `/battles/${battleId}/rounds/${roundId}/tracks/${trackId}/reactions`,
                payload
            );
            return data;
        } catch (error) {
            console.error(`[trackReactionsApi.add] Failed to add reaction to track ${trackId}:`, error.message);
            throw error;
        }
    },

    /**
     * Получить список всех реакций на трек.
     * @param {string} battleId — UUID баттла
     * @param {string} roundId — UUID раунда
     * @param {string} trackId — UUID трека
     * @returns {Promise<TrackReactionResponse[]>}
     */
    list: async (battleId, roundId, trackId) => {
        validateId(battleId, 'Battle ID');
        validateId(roundId, 'Round ID');
        validateId(trackId, 'Track ID');
        try {
            const { data } = await api.get(
                `/battles/${battleId}/rounds/${roundId}/tracks/${trackId}/reactions`
            );
            return data;
        } catch (error) {
            console.error(`[trackReactionsApi.list] Failed to fetch reactions for track ${trackId}:`, error.message);
            throw error;
        }
    },

    /**
     * Получить статистику реакций по emoji для трека.
     * @param {string} battleId — UUID баттла
     * @param {string} roundId — UUID раунда
     * @param {string} trackId — UUID трека
     * @returns {Promise<TrackReactionStatsResponse>}
     */
    getStats: async (battleId, roundId, trackId) => {
        validateId(battleId, 'Battle ID');
        validateId(roundId, 'Round ID');
        validateId(trackId, 'Track ID');
        try {
            const { data } = await api.get(
                `/battles/${battleId}/rounds/${roundId}/tracks/${trackId}/reactions/stats`
            );
            return data;
        } catch (error) {
            console.error(`[trackReactionsApi.getStats] Failed to fetch reaction stats for track ${trackId}:`, error.message);
            throw error;
        }
    },

    /**
     * Удалить конкретную emoji-реакцию текущего пользователя с трека.
     * @param {string} battleId — UUID баттла
     * @param {string} roundId — UUID раунда
     * @param {string} trackId — UUID трека
     * @param {string} emoji — Emoji-символ (например 🔥)
     * @returns {Promise<void>}
     */
    removeOne: async (battleId, roundId, trackId, emoji) => {
        validateId(battleId, 'Battle ID');
        validateId(roundId, 'Round ID');
        validateId(trackId, 'Track ID');
        if (!emoji) throw new Error('emoji is required');
        try {
            await api.delete(
                `/battles/${battleId}/rounds/${roundId}/tracks/${trackId}/reactions/${encodeURIComponent(emoji)}`
            );
        } catch (error) {
            console.error(`[trackReactionsApi.removeOne] Failed to remove reaction ${emoji} from track ${trackId}:`, error.message);
            throw error;
        }
    },

    /**
     * Удалить все реакции текущего пользователя с трека.
     * @param {string} battleId — UUID баттла
     * @param {string} roundId — UUID раунда
     * @param {string} trackId — UUID трека
     * @returns {Promise<void>}
     */
    removeAll: async (battleId, roundId, trackId) => {
        validateId(battleId, 'Battle ID');
        validateId(roundId, 'Round ID');
        validateId(trackId, 'Track ID');
        try {
            await api.delete(
                `/battles/${battleId}/rounds/${roundId}/tracks/${trackId}/reactions`
            );
        } catch (error) {
            console.error(`[trackReactionsApi.removeAll] Failed to remove all reactions from track ${trackId}:`, error.message);
            throw error;
        }
    }
};

export default trackReactionsApi;
